
import { BuildingOffice2Icon, EnvelopeIcon, PhoneIcon, ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/outline';
import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import InputMask from 'react-input-mask';
import { useTranslation } from 'react-i18next';
import CookieBanner from '../components/CookieBanner';
import ErrorMessage from "../components/ErrorMessage";
import SuccessMessage from '../components/SuccessMessage';
import { brand } from '../config/brand';

export default function Contacts() {
  const { t } = useTranslation();
  const [isChecked, setIsChecked] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const form = useRef();

  const handlePhoneNumberInput = (event) => {
    const input = event.target.value;
    const inputDigits = input.replace(/\D/g, "");

    const formattedPhoneNumber = inputDigits.replace(
      /^(\d{1})(\d{3})(\d{3})(\d{2})(\d{2})$/,
      "+$1($2)$3-$4-$5"
    );

    setPhoneNumber(formattedPhoneNumber);
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!form.current.checkValidity() || !isChecked) {
      setErrorMessage(t('errors-contacts-empty-input'));
      return;
    }

    const formData = new FormData(form.current);
    const payload = {
      contact_message: {
        first_name: formData.get('first_name'),
        last_name: formData.get('last_name'),
        organization: formData.get('organization'),
        email: formData.get('email'),
        phone: formData.get('phone'),
        message: formData.get('message'),
        website: formData.get('website'),
      },
    };

    setIsLoading(true);

    try {
      const response = await fetch('/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Contact form request failed with status ${response.status}`);
      }

      setIsEmailSent(true);
    } catch (_error) {
      setErrorMessage(t('errors-contacts-not-sent'));
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-night-900 text-ink-100">
        <svg className="mr-3 h-5 w-5 animate-spin text-signal-500" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647zM20 12a8 8 0 01-8 8v-4a4 4 0 004-4h4zm-2-7.938A7.962 7.962 0 0120 12h4c0-3.042-1.135-5.824-3-7.938l-3 2.647z" />
        </svg>
        <span className="text-lg">{t('contacts-form-sending')}</span>
      </div>
    );
  }

  if (isEmailSent) {
    return (
      <div className="isolate px-6 py-32 sm:py-40 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <SuccessMessage message={t('contacts-message-sent')} />
        </div>
      </div>
    );
  }

  return (
    <div className="relative isolate">
      <div className="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-2">
        <div className="relative px-6 pt-32 pb-20 sm:pt-40 lg:static lg:py-48 lg:px-8">
          <div className="mx-auto max-w-xl lg:mx-0 lg:max-w-lg">
            <div className="absolute inset-y-0 left-0 -z-10 w-full overflow-hidden border-r border-white/5 bg-night-800/60 lg:w-1/2">
              <svg
                className="absolute inset-0 h-full w-full stroke-white/5 [mask-image:radial-gradient(100%_100%_at_top_right,white,transparent)]"
                aria-hidden="true"
              >
                <defs>
                  <pattern
                    id="83fd4e5a-9d52-42fc-97b6-718e5d7ee527"
                    width={200}
                    height={200}
                    x="100%"
                    y={-1}
                    patternUnits="userSpaceOnUse"
                  >
                    <path d="M130 200V.5M.5 .5H200" fill="none" />
                  </pattern>
                </defs>
                <svg x="100%" y={-1} className="overflow-visible fill-signal-500/5">
                  <path d="M-470.5 0h201v201h-201Z" strokeWidth={0} />
                </svg>
                <rect width="100%" height="100%" strokeWidth={0} fill="url(#83fd4e5a-9d52-42fc-97b6-718e5d7ee527)" />
              </svg>
            </div>
            <h2 className="font-display text-4xl font-extrabold uppercase italic tracking-tight text-ink-100" data-testid="main-contacts-header">{t('contacts-header')}</h2>
            <p className="mt-6 text-lg leading-8 text-ink-300">
              {t('contacts-questions-text')}
            </p>
            <dl className="mt-10 space-y-4 text-base leading-7 text-ink-300">
              {brand.contact.address && <div className="flex gap-x-4">
                <dt className="flex-none">
                  <span className="sr-only">{t('contacts-questions-address')}</span>
                  <BuildingOffice2Icon className="h-7 w-6 text-signal-500" aria-hidden="true" />
                </dt>
                <dd>
                {t('contacts-questions-country-text')}
                  <br />
                  {brand.contact.address}
                </dd>
              </div>}
              {brand.contact.phone && <div className="flex gap-x-4">
                <dt className="flex-none">
                  <span className="sr-only">{t('contacts-questions-phone')}</span>
                  <PhoneIcon className="h-7 w-6 text-signal-500" aria-hidden="true" />
                </dt>
                <dd>
                  <div>
                    <a className="transition-colors hover:text-signal-400" href={`tel:${brand.contact.phone.replace(/\s/g, '')}`}>
                      {brand.contact.phone}
                    </a>
                  </div>
                </dd>
              </div>}
              {brand.contact.email && <div className="flex gap-x-4">
                <dt className="flex-none">
                  <span className="sr-only">{t('contacts-questions-email')}</span>
                  <EnvelopeIcon className="h-7 w-6 text-signal-500" aria-hidden="true" />
                </dt>
                <dd>
                  <div>
                    <a className="transition-colors hover:text-signal-400" href={`mailto:${brand.contact.email}`}>
                      {brand.contact.email}
                    </a>
                  </div>
                </dd>
              </div>}
              {brand.contact.whatsappUrl && <div className="flex gap-x-4">
                <dt className="flex-none">
                  <span className="sr-only">{t('contacts-questions-whatsapp')}</span>
                  <ChatBubbleBottomCenterTextIcon className="h-7 w-6 text-signal-500" aria-hidden="true" />
                </dt>
                <dd>
                  <div>
                    <a className="transition-colors hover:text-signal-400 hover:underline" href={brand.contact.whatsappUrl}>
                      <span className="underline inline-block align-middle">WhatsApp</span>
                    </a>
                  </div>
                </dd>
              </div>}
            </dl>
            <div className="mt-10 rounded-2xl border border-signal-500/20 bg-night-700/50 p-5">
              <h3 className="font-display text-xl font-bold uppercase italic tracking-wide text-signal-400">
                {t('contacts-requisites-title')}
              </h3>
              <dl className="mt-5 space-y-4 text-sm leading-6 text-ink-300">
                <div className="grid gap-1 sm:grid-cols-[7rem_1fr] sm:gap-4">
                  <dt className="font-semibold uppercase tracking-wide text-ink-400">
                    {t('contacts-requisites-legal-name')}
                  </dt>
                  <dd className="font-semibold text-ink-100">{brand.legal.name}</dd>
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
                  <div>
                    <dt className="font-semibold uppercase tracking-wide text-ink-400">
                      {t('contacts-requisites-bin')}
                    </dt>
                    <dd className="mt-1 font-medium text-ink-100">{brand.legal.bin}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold uppercase tracking-wide text-ink-400">
                      {t('contacts-requisites-postal-code')}
                    </dt>
                    <dd className="mt-1 font-medium text-ink-100">{brand.legal.postalCode}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold uppercase tracking-wide text-ink-400">
                      {t('contacts-requisites-kbe')}
                    </dt>
                    <dd className="mt-1 font-medium text-ink-100">{brand.legal.kbe}</dd>
                  </div>
                </div>
                <div className="grid gap-1 sm:grid-cols-[7rem_1fr] sm:gap-4">
                  <dt className="font-semibold uppercase tracking-wide text-ink-400">
                    {t('contacts-requisites-director')}
                  </dt>
                  <dd className="font-medium text-ink-100">{brand.legal.director}</dd>
                </div>
                <div className="space-y-4 border-t border-white/10 pt-4">
                  {brand.legal.bankAccounts.map((account) => (
                    <div key={account.iik} data-testid="legal-bank-account" className="rounded-xl bg-night-800/60 p-4">
                      <div className="grid gap-1 sm:grid-cols-[4rem_1fr] sm:gap-3">
                        <dt className="font-semibold uppercase tracking-wide text-ink-400">
                          {t('contacts-requisites-bank')}
                        </dt>
                        <dd className="font-semibold text-ink-100">{account.bank}</dd>
                      </div>
                      <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        <div>
                          <dt className="font-semibold uppercase tracking-wide text-ink-400">
                            {t('contacts-requisites-iik')}
                          </dt>
                          <dd className="mt-1 break-all font-medium text-ink-100">{account.iik}</dd>
                        </div>
                        <div>
                          <dt className="font-semibold uppercase tracking-wide text-ink-400">
                            {t('contacts-requisites-bik')}
                          </dt>
                          <dd className="mt-1 font-medium text-ink-100">{account.bik}</dd>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </dl>
            </div>
          </div>
        </div>
        <form ref={form} onSubmit={sendEmail} className="px-6 pb-24 pt-20 sm:pb-32 lg:py-48 lg:px-8">
      {errorMessage && (
          <ErrorMessage message={errorMessage} />
        )}
          <div className="mx-auto max-w-xl lg:mr-0 lg:max-w-lg">
            <div className="grid grid-cols-1 gap-y-6 gap-x-8 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="first-name"
                  className="block text-sm font-semibold uppercase tracking-wide leading-6 text-ink-200"
                >
                  {t('contacts-form-first-name')}
                </label>
                <div className="mt-2.5">
                  <input
                    type="text"
                    name="first_name"
                    id="first-name"
                    autoComplete="given-name"
                    required
                    maxLength={80}
                    className="block w-full rounded-lg border-0 bg-night-700/70 py-2.5 px-3.5 text-ink-100 shadow-sm ring-1 ring-inset ring-white/10 placeholder:text-ink-400 focus:ring-2 focus:ring-inset focus:ring-signal-500 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="last-name"
                  className="block text-sm font-semibold uppercase tracking-wide leading-6 text-ink-200"
                >
                  {t('contacts-form-last-name')}
                </label>
                <div className="mt-2.5">
                  <input
                    type="text"
                    name="last_name"
                    id="last-name"
                    autoComplete="family-name"
                    required
                    maxLength={80}
                    className="block w-full rounded-lg border-0 bg-night-700/70 py-2.5 px-3.5 text-ink-100 shadow-sm ring-1 ring-inset ring-white/10 placeholder:text-ink-400 focus:ring-2 focus:ring-inset focus:ring-signal-500 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="company"
                  className="block text-sm font-semibold uppercase tracking-wide leading-6 text-ink-200"
                >
                  {t('contacts-form-organization')}
                </label>
                <div className="mt-2.5">
                  <input
                    type="text"
                    name="organization"
                    id="company"
                    autoComplete="organization"
                    required
                    maxLength={200}
                    className="block w-full rounded-lg border-0 bg-night-700/70 py-2.5 px-3.5 text-ink-100 shadow-sm ring-1 ring-inset ring-white/10 placeholder:text-ink-400 focus:ring-2 focus:ring-inset focus:ring-signal-500 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold uppercase tracking-wide leading-6 text-ink-200"
                >
                  {t('contacts-form-email')}
                </label>
                <div className="mt-2.5">
                  <input
                    type="email"
                    name="email"
                    id="email"
                    required
                    maxLength={254}
                    autoComplete="email"
                    className="block w-full rounded-lg border-0 bg-night-700/70 py-2.5 px-3.5 text-ink-100 shadow-sm ring-1 ring-inset ring-white/10 placeholder:text-ink-400 focus:ring-2 focus:ring-inset focus:ring-signal-500 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="phone-number"
                  className="block text-sm font-semibold uppercase tracking-wide leading-6 text-ink-200"
                >
                  {t('contacts-form-phone')}
                </label>
                <div className="mt-2.5">
                  <InputMask
                    mask="+7(999)999-99-99"
                    maskChar="_"
                    type="tel"
                    name="phone"
                    id="phone-number"
                    value={phoneNumber}
                    onChange={handlePhoneNumberInput}
                    required
                    autoComplete="tel"
                    className="block w-full rounded-lg border-0 bg-night-700/70 py-2.5 px-3.5 text-ink-100 shadow-sm ring-1 ring-inset ring-white/10 placeholder:text-ink-400 focus:ring-2 focus:ring-inset focus:ring-signal-500 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="message"
                  className="block text-sm font-semibold uppercase tracking-wide leading-6 text-ink-200"
                >
                  {t('contacts-form-message')}
                </label>
                <div className="mt-2.5">
                  <textarea
                    name="message"
                    id="message"
                    rows={4}
                    required
                    maxLength={5000}
                    className="block w-full rounded-lg border-0 bg-night-700/70 py-2.5 px-3.5 text-ink-100 shadow-sm ring-1 ring-inset ring-white/10 placeholder:text-ink-400 focus:ring-2 focus:ring-inset focus:ring-signal-500 sm:text-sm sm:leading-6"
                    defaultValue={""}
                  />
                </div>
              </div>
              <div className="absolute -left-[10000px]" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  name="website"
                  id="website"
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
              <div className="flex gap-x-4 sm:col-span-2">
                <div className="flex h-6 items-center">
                  <button
                    type="button"
                    className={`flex w-8 flex-none cursor-pointer rounded-full p-px ring-1 ring-inset ring-white/10 transition-colors duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal-500 ${
                      isChecked ? "bg-signal-500" : "bg-night-500"
                    }`}
                    role="switch"
                    aria-checked={isChecked}
                    aria-labelledby="switch-1-label"
                    onClick={() => setIsChecked(!isChecked)}
                  >
                    <span className="sr-only">{t('contacts-form-agreement')}</span>
                    <span
                      aria-hidden="true"
                      className={`h-4 w-4 transform rounded-full bg-white shadow-sm ring-1 ring-night-900/20 transition duration-200 ease-in-out ${
                        isChecked ? "translate-x-3.5" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
                <label className="text-sm leading-6 text-ink-300" id="switch-1-label">
                  {t('contacts-data-processing-agreement')}
                  <Link to="/privacyAgreement" className="font-semibold text-signal-400 hover:text-signal-300" target = '_blank'>
                    {t('contacts-privacy-policy-link')}
                  </Link>
                  .
                </label>
              </div>
            </div>
            <div className="mt-8 flex justify-end">
              <button
                type="submit"
                className="rounded-full bg-signal-500 px-7 py-3 text-center text-sm font-bold uppercase tracking-wide text-white shadow-glow transition-colors hover:bg-signal-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal-500"
              >
                {t('contacts-form-send')}
              </button>
            </div>
          </div>
        </form>
      </div>
      <CookieBanner />
    </div>
  )
}
